import React, { useState, useEffect, useContext } from 'react';
import { NotificationContext } from '../../context/NotificationContext';
import api from '../../api';

const RequestContentManager = ({ configId }) => {
  const { showNotification } = useContext(NotificationContext);
  const [requestContents, setRequestContents] = useState([]);
  const [requestContentTypes, setRequestContentTypes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    request_content_name: '',
    request_content_display_name: '',
    request_content_description: '',
    request_content_format: '',
    request_content_type_id: '',
    date_needed: false,
    display_order: ''
  });

  useEffect(() => {
    loadRequestContents();
    loadRequestContentTypes();
  }, [configId]);

  const loadRequestContents = async () => {
    try {
      const response = await api.get(`/admin/request_content/${configId}`);
      const sortedContents = response.data.sort((a, b) => {
        const orderA = a.display_order === null ? Infinity : a.display_order;
        const orderB = b.display_order === null ? Infinity : b.display_order;
        return orderA - orderB;
      });
      setRequestContents(sortedContents);
    } catch (error) {
      showNotification('Error loading request contents: ' + error.message, true);
    }
  };

  const loadRequestContentTypes = async () => {
    try {
      const response = await api.get('/enum/request-content-type/');
      setRequestContentTypes(response.data);
    } catch (error) {
      showNotification('Error loading request content types: ' + error.message, true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submissionData = {
        configuration_id: parseInt(configId),
        request_content_name: formData.request_content_name,
        request_content_display_name: formData.request_content_display_name,
        request_content_description: formData.request_content_description || undefined,
        request_content_format: formData.request_content_format || undefined,
        request_content_type_id: parseInt(formData.request_content_type_id),
        date_needed: formData.date_needed,
        display_order: formData.display_order ? parseInt(formData.display_order) : null
      };

      await api.post('/admin/request_content/', submissionData);
      showNotification('Request content added successfully!');
      setIsModalOpen(false);
      setFormData({
        request_content_name: '',
        request_content_display_name: '',
        request_content_description: '',
        request_content_format: '',
        request_content_type_id: '',
        date_needed: false,
        display_order: ''
      });
      loadRequestContents();
    } catch (error) {
      showNotification('Failed to add request content: ' + error.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this request content?')) return;

    try {
      await api.delete(`/admin/request_content/${id}`);
      showNotification('Request content deleted successfully!');
      loadRequestContents();
    } catch (error) {
      showNotification('Failed to delete request content: ' + error.message, true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const getContentTypeName = (typeId) => {
    const type = requestContentTypes.find(t => t.id === typeId);
    return type ? type.name : '-';
  };

  return (
    <div className="section-card">
      <div className="section-header">
        <h2>Request Content</h2>
        <button 
          className="add-entity-btn"
          onClick={() => setIsModalOpen(true)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/>
          </svg>
          <span>Add Request Content</span>
        </button>
      </div>
      
      <div className="section-content">
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Display Name</th>
                <th>Description</th>
                <th>Format</th>
                <th>Content Type</th>
                <th>Date Needed</th>
                <th>Order</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requestContents.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-gray-500">
                    No request content found.
                  </td>
                </tr>
              ) : (
                requestContents.map(content => (
                  <tr key={content.id}>
                    <td>{content.request_content_name}</td>
                    <td>{content.request_content_display_name}</td>
                    <td>{content.request_content_description || '-'}</td>
                    <td>{content.request_content_format || '-'}</td>
                    <td>{getContentTypeName(content.request_content_type_id)}</td>
                    <td>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        content.date_needed ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {content.date_needed ? 'Required' : 'Not Required'}
                      </span>
                    </td>
                    <td>{content.display_order !== null ? content.display_order : '-'}</td>
                    <td>
                      <button 
                        onClick={() => handleDelete(content.id)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="section-modal">
          <button 
            className="modal-close"
            onClick={() => setIsModalOpen(false)}
          >
            ×
          </button>
          <h3>Add Request Content</h3>
          <form onSubmit={handleSubmit}>
            <div>
              <label className="form-label">Name</label>
              <input
                type="text"
                name="request_content_name"
                value={formData.request_content_name}
                onChange={handleInputChange}
                className="form-input"
                required
              />
            </div>
            <div>
              <label className="form-label">Display Name</label>
              <input
                type="text"
                name="request_content_display_name"
                value={formData.request_content_display_name}
                onChange={handleInputChange}
                className="form-input"
                required
              />
            </div>
            <div>
              <label className="form-label">Description</label>
              <input
                type="text"
                name="request_content_description"
                value={formData.request_content_description}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Format</label>
              <input
                type="text"
                name="request_content_format"
                value={formData.request_content_format}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Display Order</label>
              <input
                type="number"
                name="display_order"
                value={formData.display_order}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter order (e.g., 1, 2, 3)"
              />
            </div>
            <div>
              <label className="form-label">Content Type</label>
              <select
                name="request_content_type_id"
                value={formData.request_content_type_id}
                onChange={handleInputChange}
                className="form-input"
                required
              >
                <option value="">Select a content type</option>
                {requestContentTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="date_needed"
                  checked={formData.date_needed}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span>Date Needed</span>
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setIsModalOpen(false)}>
                Cancel
              </button>
              <button type="submit" disabled={loading}>
                {loading ? 'Adding...' : 'Add Request Content'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default RequestContentManager; 